import { ReactNode, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import {
  CheckCircle2,
  Compass as CompassIcon,
  Map as MapIcon,
  Moon,
  Plus,
  Sprout,
} from "lucide-react";

import { cn } from "../../../lib/utils";
import { queryClient } from "../../../lib/queryClient";
import { movementLine } from "../../../lib/movementLine";
import { wakeDecision, swapPatches } from "../../../lib/activeCap";
import { readCockpitState } from "../../../lib/cockpitState";
import {
  graduationReady,
  readGraduateHintSeen,
  markGraduateHintSeen,
} from "../../../lib/graduationGate";
import useRetryFetch from "../../../hooks/useRetryFetch";
import { useNextStepsByProject } from "../../../hooks/useNextStepsByProject";
import { useSpaces } from "../../../context/SpaceProvider";
import {
  listPwfs,
  createPwf,
  updatePwf,
  getQuestSummary,
  type Pwf,
} from "../../../api/pwf";
import { Button } from "../../ui/button";
import { Skeleton } from "../../ui/skeleton";
import { Slider } from "../../ui/slider";
import { Input } from "../../ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../ui/dialog";
import { scrollReveal } from "../motionConstants";
import { SeedMoreButton } from "./LifeOsSeeder";
import { WeeklyReviewBanner } from "./WeeklyReviewBanner";
import { SwapDialog } from "./SwapDialog";
import GraduationSheet from "./GraduationSheet";
import ClaryNudge from "./ClaryNudge";
import { listTakeaways, type UserTakeaway } from "../../../api/userTakeaways";

// ── The calibration lens ──────────────────────────────────────────────────────
// Three honest sliders — no visible arithmetic: Pull / Readiness / Cost of
// waiting. This is the platform default; framework packs may later ship their
// own lens, so answers are stored per-key rather than as a total.

export const LENS_KEYS = ["pull", "readiness", "costOfWaiting"] as const;
export type LensKey = (typeof LENS_KEYS)[number];
export type Lens = Record<LensKey, number>;

export interface LifeProjectConfig {
  lens?: Lens;
  /** Legacy five-question answers from before the lens — kept so old projects still render. */
  scores?: Record<string, number>;
  status: "promote" | "someday";
  timeRange?: string;
  /** Seeder-preserved context (experience v3) — the room's "why" card + area link */
  whyNow?: string;
  spaceName?: string;
  spaceId?: string;
  journeySuggestedReason?: string;
  /** Optional scheduled wake (ISO date) — "resting with a date feels held" (v3 §3.b.3) */
  wakeAt?: string;
}

const DEFAULT_LENS: Lens = { pull: 3, readiness: 3, costOfWaiting: 3 };
export const ACTIVE_CAP = 3;
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

export const lifeProjectOf = (p: Pwf): LifeProjectConfig | null =>
  ((p.config as any)?.lifeProject as LifeProjectConfig) ?? null;

// Projects calibrated before the lens map their old answers onto it; the next
// recalibration saves in the new shape.
export const lensOf = (lp: LifeProjectConfig): Lens =>
  lp.lens ?? {
    pull: lp.scores?.want ?? 3,
    readiness: lp.scores?.startNow ?? 3,
    costOfWaiting: lp.scores?.regret ?? 3,
  };

const lensWeight = (l: Lens) => LENS_KEYS.reduce((n, k) => n + (l[k] ?? 0), 0);

// Active takes conviction on both axes; everything else rests, guilt-free.
const suggestPlacement = (l: Lens): "active" | "resting" =>
  l.pull >= 4 && l.readiness >= 4 ? "active" : "resting";

export const isJourneyPwf = (p: Pwf) => Boolean((p.config as any)?.studio?.isJourney);

// ── Component ─────────────────────────────────────────────────────────────────

const LifeProjects = ({ headerExtra }: { headerExtra?: ReactNode }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const retryFetch = useRetryFetch();
  const { spaces } = useSpaces();

  // Area↔color, consistently across seats (spec §3.b.1) — same lookup PlantedCard uses.
  const spaceColor = (name?: string) =>
    (spaces ?? []).find((s: any) => s.name === name)?.color || "#6366f1";

  const { data: pwfs, isLoading } = useQuery({
    queryKey: ["cockpit-projects"],
    queryFn: () => listPwfs(retryFetch),
    staleTime: 30_000,
  });

  const projects = useMemo(
    () =>
      (pwfs ?? [])
        .filter(
          (p) =>
            !p.isTemplate &&
            !p.pwfMeta?.fiParent &&
            !["archived", "completed"].includes(p.lifecycleState) &&
            lifeProjectOf(p)
        )
        .sort(
          (a, b) =>
            lensWeight(lensOf(lifeProjectOf(b)!)) - lensWeight(lensOf(lifeProjectOf(a)!))
        ),
    [pwfs]
  );

  // Per-user cap (config.cockpit.activeCap via the ritual) — ACTIVE_CAP stays
  // the default for callers that need a constant.
  const activeCap = readCockpitState(pwfs ?? []).activeCap;

  const promoted = projects.filter((p) => lifeProjectOf(p)!.status === "promote");
  const someday = projects.filter((p) => lifeProjectOf(p)!.status === "someday");
  const freeSlots = Math.max(0, activeCap - promoted.length);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Pwf | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [graduating, setGraduating] = useState<Pwf | null>(null);
  const [swapFor, setSwapFor] = useState<Pwf | null>(null);

  // The manager's next step per project (Plan A — shared home-today-actions cache)
  const { nextStepByProject } = useNextStepsByProject();

  // Roots — how many captures feed each project (takeaways bridged via fiPwf)
  const { data: takeawaysData } = useQuery({
    queryKey: ["lifeos-roots"],
    queryFn: () => listTakeaways(retryFetch, { limit: 200 }),
    staleTime: 60_000,
  });
  const rootsByProject = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const tk of (takeawaysData as any)?.list ?? []) {
      const pid = (tk as any).pwfMeta?.fiPwf;
      // Roots are insights only (spec §3.d) — seeded action steps also carry
      // pwfMeta.fiPwf but shouldn't inflate the "roots feeding this project" count.
      if (pid && (tk as any).type === "insight") counts[pid] = (counts[pid] ?? 0) + 1;
    }
    return counts;
  }, [takeawaysData]);

  // Journey phase progress — graduated projects show where their path stands
  const journeyIds = useMemo(
    () => projects.filter(isJourneyPwf).map((p) => p._id),
    [projects]
  );
  const { data: phasesByJourney } = useQuery({
    queryKey: ["cockpit-project-phases", journeyIds.join(",")],
    enabled: journeyIds.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      const settled = await Promise.allSettled(
        journeyIds.map((id) => getQuestSummary(retryFetch, id))
      );
      const map: Record<string, { completed: number; total: number }> = {};
      settled.forEach((s, i) => {
        if (s.status === "fulfilled" && (s.value.phases?.length ?? 0) > 0) {
          map[journeyIds[i]] = {
            completed: s.value.phases.filter(
              (ph) => ph.state === "completed" || !!ph.completedAt
            ).length,
            total: s.value.phases.length,
          };
        }
      });
      return map;
    },
  });

  // The week's motion, project-flavored — all derived from the takeaways we
  // already hold, no extra round-trips.
  const weekStats = useMemo(() => {
    const cutoff = Date.now() - WEEK_MS;
    let actionsDone = 0;
    let insightsNew = 0;
    let rootsNew = 0;
    for (const tk of ((takeawaysData as any)?.list ?? []) as any[]) {
      if (tk.type === "action" && tk.status === "completed" && (tk.completedOn ?? 0) >= cutoff)
        actionsDone++;
      if (tk.type === "insight" && (tk.createdOn ?? 0) >= cutoff) insightsNew++;
      // Roots are insights only (spec §3.d) — see rootsByProject above.
      if (tk.type === "insight" && (tk.createdOn ?? 0) >= cutoff && tk.pwfMeta?.fiPwf) rootsNew++;
    }
    return { actionsDone, insightsNew, rootsNew };
  }, [takeawaysData]);

  // Per-project completion stats, feeding the movement line (spec §3.b.1) —
  // same ["lifeos-roots"] takeaways weekStats already reads completed
  // actions from above, just bucketed by project instead of summed across all.
  const completionStatsByProject = useMemo(() => {
    const cutoff = Date.now() - WEEK_MS;
    const today = new Date();
    const stats: Record<string, { stepsCompletedThisWeek: number; stepsCompletedTotal: number; completedToday: boolean }> = {};
    for (const tk of ((takeawaysData as any)?.list ?? []) as any[]) {
      const pid = tk.pwfMeta?.fiPwf;
      if (!pid || tk.type !== "action" || tk.status !== "completed" || !tk.completedOn) continue;
      const entry = stats[pid] ?? { stepsCompletedThisWeek: 0, stepsCompletedTotal: 0, completedToday: false };
      // Capped by the shared ["lifeos-roots"] query's limit: 200 (newest-first), so this can
      // undercount lifetime totals for veteran/prolific accounts — only delays graduationGate.ts's gate, never triggers it early.
      entry.stepsCompletedTotal++;
      if (tk.completedOn >= cutoff) entry.stepsCompletedThisWeek++;
      const completedAt = new Date(tk.completedOn);
      if (
        completedAt.getFullYear() === today.getFullYear() &&
        completedAt.getMonth() === today.getMonth() &&
        completedAt.getDate() === today.getDate()
      )
        entry.completedToday = true;
      stats[pid] = entry;
    }
    return stats;
  }, [takeawaysData]);

  const movementByProject = useMemo(() => {
    const map: Record<string, ReturnType<typeof movementLine>> = {};
    for (const p of projects) {
      const stats = completionStatsByProject[p._id];
      map[p._id] = movementLine({
        lastActivityAt: p.lastActivityAt,
        stepsCompletedThisWeek: stats?.stepsCompletedThisWeek ?? 0,
        completedToday: stats?.completedToday ?? false,
        createdAt: p.createdAt ?? null,
      });
    }
    return map;
  }, [projects, completionStatsByProject]);

  // A resting project quietly gathering roots is the honest recalibration cue.
  const nudge = useMemo(() => {
    const gathering = someday
      .map((p) => ({ p, roots: rootsByProject[p._id] ?? 0 }))
      .filter((x) => x.roots > 0)
      .sort((a, b) => b.roots - a.roots)[0];
    if (gathering) return { kind: "roots" as const, ...gathering };
    if (freeSlots > 0 && someday.length > 0) return { kind: "slot" as const };
    return null;
  }, [someday, freeSlots, rootsByProject]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["cockpit-projects"] });

  const setStatus = async (p: Pwf, status: "promote" | "someday") => {
    if (busyId) return;
    if (status === "promote" && wakeDecision(promoted.length, activeCap) === "swap") {
      // Never silently exceed the cap, never hard-block — ask which one rests (spec §3.b.4).
      setSwapFor(p);
      return;
    }
    setBusyId(p._id);
    try {
      // Waking clears the schedule — a promoted project shouldn't carry a
      // stale wake month back to the shelf if it's ever rested again.
      await updatePwf(retryFetch, p._id, {
        config: {
          lifeProject:
            status === "promote"
              ? { ...lifeProjectOf(p)!, status, wakeAt: undefined }
              : { ...lifeProjectOf(p)!, status },
        },
      });
      toast.success(
        status === "someday" ? t("cockpit_moved_resting") : t("cockpit_activated")
      );
      refresh();
    } catch (e: any) {
      toast.error(e.message ?? "Could not update");
    } finally {
      setBusyId(null);
    }
  };

  const swap = async (rest: Pwf) => {
    const wake = swapFor;
    if (!wake || busyId) return;
    setBusyId(wake._id);
    try {
      const patches = swapPatches(lifeProjectOf(wake)!, lifeProjectOf(rest)!);
      await updatePwf(retryFetch, rest._id, { config: { lifeProject: patches.rest } });
      await updatePwf(retryFetch, wake._id, { config: { lifeProject: patches.wake } });
      toast.success(t("cockpit_swapped", { wake: wake.title, rest: rest.title }));
      setSwapFor(null);
      refresh();
    } catch (e: any) {
      toast.error(e.message ?? "Could not update");
    } finally {
      setBusyId(null);
    }
  };

  // Graduation: the project IS already a pwf — becoming a journey means the
  // system drafts the phased path FROM the project's accumulated material
  // (captures, lens, actions), the user reviews it, and only then does the
  // studio machinery (phases, BECOME, quest map) materialize.
  const graduate = (p: Pwf) => {
    const studio = ((p.config as any)?.studio ?? {}) as Record<string, any>;
    if (studio.isJourney) {
      navigate(`/pwf?map=${p._id}`);
      return;
    }
    setGraduating(p);
  };

  const archiveProject = async (p: Pwf) => {
    if (busyId) return;
    setBusyId(p._id);
    try {
      await updatePwf(retryFetch, p._id, { lifecycleState: "completed" });
      toast.success(t("cockpit_project_done", { title: p.title }));
      refresh();
    } catch (e: any) {
      toast.error(e.message ?? "Could not update");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <motion.section {...scrollReveal} className="space-y-5">
      {/* Header — page title left, mode switch and creation on the right (design 5a) */}
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            {t("cockpit_projects_title")}
          </h1>
          <p className="text-[13px] text-muted-foreground mt-0.5">
            {t("cockpit_projects_subtitle", { count: activeCap })}
          </p>
        </div>
        {/* Mode switch stays rightmost in every mode — the projects-only
            actions slot in before it so it never moves. */}
        <div className="flex flex-wrap items-center gap-2 pb-1">
          <SeedMoreButton />
          <Button
            onClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
            className="rounded-full"
          >
            <Plus className="w-4 h-4 me-1.5" />
            {t("cockpit_new_project")}
          </Button>
          {headerExtra}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_332px] gap-5 items-start">
        {/* Main column — the board itself */}
        <div className="min-w-0 space-y-5">
          <WeeklyReviewBanner />
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-24 w-full rounded-2xl bg-gray-100" />
              <Skeleton className="h-24 w-full rounded-2xl bg-gray-100" />
            </div>
          ) : projects.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-primary/20 p-10 text-center">
              <CompassIcon className="w-8 h-8 mx-auto mb-3 text-primary/40" />
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                {t("cockpit_projects_empty")}
              </p>
            </div>
          ) : (
            <>
              {/* Active */}
              <div className="space-y-2.5">
                <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/70 flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500 inline-block" />
                  {t("cockpit_active", { count: promoted.length, cap: activeCap })}
                </p>
                {promoted.length === 0 && (
                  <p className="text-sm text-muted-foreground/70 italic px-1">
                    {t("cockpit_active_empty")}
                  </p>
                )}
                {promoted.map((p) => (
                  <ProjectCard
                    key={p._id}
                    p={p}
                    roots={rootsByProject[p._id] ?? 0}
                    phases={phasesByJourney?.[p._id]}
                    nextStep={nextStepByProject[p._id]}
                    movement={movementByProject[p._id]}
                    spaceColor={spaceColor}
                    busy={busyId === p._id}
                    onEdit={() => {
                      setEditing(p);
                      setDialogOpen(true);
                    }}
                    onToSomeday={() => setStatus(p, "someday")}
                    onDone={() => archiveProject(p)}
                    onOpen={() => navigate(`/project/${p._id}`)}
                    onGraduate={() => graduate(p)}
                    onMap={() => navigate(`/pwf?map=${p._id}`)}
                    graduationReady={graduationReady({
                      journeySuggestedReason: lifeProjectOf(p)?.journeySuggestedReason,
                      createdAt: p.createdAt,
                      stepsCompleted: completionStatsByProject[p._id]?.stepsCompletedTotal ?? 0,
                      now: Date.now(),
                    })}
                  />
                ))}
                {freeSlots > 0 && (
                  <div className="rounded-2xl border-[1.5px] border-dashed border-gray-200 px-4 py-3.5 text-center text-[12.5px] text-muted-foreground/70">
                    {t("cockpit_free_slot", { count: freeSlots })}
                  </div>
                )}
              </div>

              {/* Resting — a quiet two-column shelf, not a second to-do list */}
              {someday.length > 0 && (
                <div className="space-y-2.5">
                  <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground/70 flex items-center gap-1.5">
                    <Moon className="w-3 h-3" />
                    {t("cockpit_resting", { count: someday.length })}
                  </p>
                  <p className="text-[11px] text-muted-foreground/60">{t("cockpit_wake_asks")}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {someday.map((p) => (
                      <RestingCard
                        key={p._id}
                        p={p}
                        roots={rootsByProject[p._id] ?? 0}
                        busy={busyId === p._id}
                        onPromote={() => setStatus(p, "promote")}
                        onOpen={() => navigate(`/project/${p._id}`)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Right rail — the reasoning beside the board */}
        <div className="space-y-4">
          <div className="rounded-2xl border border-border/60 bg-card p-4">
            <p className="text-[13px] font-semibold text-foreground mb-2">
              {t("cockpit_why_cap_title", { count: activeCap })}
            </p>
            <p className="text-[12.5px] leading-relaxed text-muted-foreground">
              {t("cockpit_why_cap_body")}
            </p>
          </div>

          {nudge &&
            (nudge.kind === "roots" ? (
              <ClaryNudge
                onClick={() => {
                  setEditing(nudge.p);
                  setDialogOpen(true);
                }}
              >
                {t("cockpit_clary_resting_roots", {
                  title: nudge.p.title,
                  count: nudge.roots,
                })}
              </ClaryNudge>
            ) : (
              <ClaryNudge>{t("cockpit_clary_free_slot")}</ClaryNudge>
            ))}

          <div className="rounded-2xl border border-border/60 bg-card p-4">
            <p className="text-[13px] font-semibold text-foreground mb-2.5">
              {t("cockpit_week_title")}
            </p>
            {(() => {
              const weekEmpty = weekStats.actionsDone === 0 && weekStats.insightsNew === 0 && weekStats.rootsNew === 0;
              return (
                <>
                  {weekEmpty && (
                    <p className="text-[12px] text-muted-foreground/80 leading-relaxed mb-2">{t("cockpit_week_beginning")}</p>
                  )}
                  <div className="space-y-2 text-[12.5px] text-foreground/80">
                    <div className="flex items-center justify-between">
                      <span>{t("cockpit_week_actions")}</span>
                      <span className="font-semibold text-primary tabular-nums">
                        {weekEmpty ? "—" : weekStats.actionsDone}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>{t("cockpit_week_insights")}</span>
                      <span className="font-semibold text-amber-700 tabular-nums">
                        {weekEmpty ? "—" : weekStats.insightsNew}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span title={t("cockpit_roots_tip")} className="cursor-help underline decoration-dotted decoration-muted-foreground/40">
                        {t("cockpit_week_roots")}
                      </span>
                      <span className="font-semibold tabular-nums">{weekEmpty ? "—" : weekStats.rootsNew}</span>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </div>

      <ProjectDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        promotedCount={promoted.length}
        activeCap={activeCap}
        onSaved={refresh}
      />

      <SwapDialog
        key={swapFor?._id ?? "none"}
        wake={swapFor}
        active={promoted}
        busy={busyId != null}
        onCancel={() => setSwapFor(null)}
        onConfirm={swap}
      />

      {graduating && (
        <GraduationSheet
          project={graduating}
          onClose={() => setGraduating(null)}
          onCreated={(id) => {
            setGraduating(null);
            toast.success(t("cockpit_graduated", { title: graduating.title }));
            queryClient.invalidateQueries();
            navigate(`/pwf?map=${id}`);
          }}
        />
      )}
    </motion.section>
  );
};

// ── Active project card ───────────────────────────────────────────────────────

const ProjectCard: React.FC<{
  p: Pwf;
  roots?: number;
  phases?: { completed: number; total: number };
  nextStep?: UserTakeaway;
  movement?: { key: "moved_today" | "steps_this_week" | "quiet_days"; count?: number } | null;
  spaceColor: (name?: string) => string;
  busy: boolean;
  onEdit: () => void;
  onToSomeday: () => void;
  onDone: () => void;
  onOpen: () => void;
  onGraduate: () => void;
  onMap: () => void;
  graduationReady: boolean;
}> = ({
  p,
  roots,
  phases,
  nextStep,
  movement,
  spaceColor,
  busy,
  onEdit,
  onToSomeday,
  onDone,
  onOpen,
  onGraduate,
  onMap,
  graduationReady,
}) => {
  const { t } = useTranslation();
  const [hintSeen, setHintSeen] = useState(() => readGraduateHintSeen(window.localStorage));
  const lp = lifeProjectOf(p)!;
  const lens = lensOf(lp);
  // A graduated project is a journey — its quest map is the richer view
  const isJourney = isJourneyPwf(p);
  const phaseCurrent = phases ? Math.min(phases.completed + 1, phases.total) : 0;

  return (
    <div
      onClick={onOpen}
      className="rounded-2xl border border-primary/15 bg-card px-[18px] py-[15px] transition-shadow hover:shadow-md cursor-pointer"
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onOpen();
              }}
              className="text-start group min-w-0"
            >
              <span className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">
                {p.title}
              </span>
            </button>
            {isJourney && (
              <span className="flex-none text-[10px] font-semibold text-violet-700 bg-violet-50 border border-violet-200 rounded-full px-2 py-0.5">
                {t("cockpit_journey_badge")}
              </span>
            )}
          </div>
          {lp.spaceName && (
            <p
              className="text-[10.5px] font-semibold uppercase tracking-widest mb-0.5"
              style={{ color: spaceColor(lp.spaceName) }}
            >
              {lp.spaceName}
            </p>
          )}
          {nextStep && (
            <p className="text-[12.5px] text-foreground/75 mt-1 line-clamp-1">
              <span className="text-muted-foreground">{t("cockpit_next")}</span> {nextStep.content}
            </p>
          )}
          {/* Lens fingerprint — a shape, not a score — plus the roots feeding it */}
          <div className="flex items-end gap-2.5 mt-2.5 min-w-0">
            <div className="flex items-end gap-[3px] flex-none" title={t("cockpit_lens_tip")}>
              {LENS_KEYS.map((k) => (
                <div
                  key={k}
                  className="h-6 w-[11px] rounded-[3px] bg-gray-100 overflow-hidden flex flex-col justify-end"
                >
                  <div
                    className="w-full rounded-[3px] bg-primary/70"
                    style={{ height: `${(lens[k] / 5) * 100}%` }}
                  />
                </div>
              ))}
            </div>
            {(roots ?? 0) > 0 && (
              <span
                className="inline-flex items-center gap-1 rounded-full border border-emerald-200/70 bg-emerald-50/60 px-2 py-0.5 text-[11px] text-emerald-800 flex-none"
                title={t("grad_roots_tip", { count: roots })}
              >
                <Sprout className="w-3 h-3" />
                {roots}
              </span>
            )}
            {isJourney && phases ? (
              <div className="flex items-center gap-2 flex-1 min-w-[120px] max-w-[220px] ms-auto">
                <div className="flex-1 h-1 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary"
                    style={{
                      width: `${Math.max(6, (phases.completed / phases.total) * 100)}%`,
                    }}
                  />
                </div>
                <span className="text-[11px] text-muted-foreground whitespace-nowrap flex-none">
                  {t("cockpit_phase_of", { current: phaseCurrent, total: phases.total })}
                </span>
              </div>
            ) : (
              <span className="text-[11px] text-muted-foreground/60 truncate hidden sm:inline">
                {t("cockpit_lens_caption")}
              </span>
            )}
          </div>
          {movement && (
            <p className="text-[11.5px] text-muted-foreground mt-1.5">
              {movement.key === "moved_today" && t("cockpit_moved_today")}
              {movement.key === "steps_this_week" && t("cockpit_steps_week", { count: movement.count })}
              {movement.key === "quiet_days" && t("cockpit_quiet_days", { count: movement.count })}
            </p>
          )}
        </div>
        {/* Actions — quiet pills, one graduation invitation */}
        <div
          className="flex flex-col items-end gap-1.5 flex-shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-1.5">
            {isJourney ? (
              <button
                onClick={onMap}
                className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-[11.5px] font-medium text-primary hover:bg-primary/10 transition-colors"
              >
                <MapIcon className="w-3 h-3" />
                {t("cockpit_open_quest_map")}
              </button>
            ) : (
              <button
                onClick={onEdit}
                className="rounded-full border border-border/70 px-3 py-1 text-[11.5px] text-muted-foreground hover:text-foreground hover:border-border transition-colors"
              >
                {t("cockpit_recalibrate")}
              </button>
            )}
            <button
              disabled={busy}
              onClick={onToSomeday}
              className="inline-flex items-center gap-1 rounded-full border border-border/70 px-3 py-1 text-[11.5px] text-muted-foreground hover:text-foreground hover:border-border transition-colors disabled:opacity-50"
            >
              <Moon className="w-3 h-3" />
              {t("cockpit_to_resting")}
            </button>
            <button
              disabled={busy}
              onClick={onDone}
              title={t("cockpit_mark_done")}
              className="p-1 rounded-full text-muted-foreground/50 hover:text-emerald-600 transition-colors disabled:opacity-50"
            >
              <CheckCircle2 className="w-4 h-4" />
            </button>
          </div>
          {isJourney ? (
            <button
              onClick={onEdit}
              className="text-[11px] text-muted-foreground/60 hover:text-foreground underline-offset-2 hover:underline"
            >
              {t("cockpit_recalibrate")}
            </button>
          ) : graduationReady ? (
            <div className="flex flex-col items-end">
              <button
                onClick={() => {
                  markGraduateHintSeen(window.localStorage);
                  setHintSeen(true);
                  onGraduate();
                }}
                className="inline-flex items-center min-h-[44px] text-[11px] text-primary/80 hover:text-primary underline underline-offset-2"
              >
                {t("cockpit_graduate")}
              </button>
              {!hintSeen && (
                <span className="text-[10.5px] text-muted-foreground/60 text-end -mt-2 mb-1">
                  {t("cockpit_graduate_hint")}
                </span>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

// ── Resting shelf card — dimmed, one honest way back up ──────────────────────

const RestingCard: React.FC<{
  p: Pwf;
  roots: number;
  busy: boolean;
  onPromote: () => void;
  onOpen: () => void;
}> = ({ p, roots, busy, onPromote, onOpen }) => {
  const { t, i18n } = useTranslation();
  const lpWakeAt = lifeProjectOf(p)?.wakeAt ?? null;
  return (
    <div
      onClick={onOpen}
      className="rounded-[14px] border border-border/70 bg-card opacity-80 hover:opacity-100 transition-opacity px-[15px] py-3 flex items-center gap-2.5 cursor-pointer"
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onOpen();
        }}
        className="flex-1 min-w-0 text-start"
      >
        <span className="text-[13px] font-medium text-foreground/80 hover:text-primary transition-colors line-clamp-1">
          {p.title}
        </span>
      </button>
      {roots > 0 && (
        <span
          className="inline-flex items-center gap-0.5 text-[11px] text-emerald-800/80 flex-none"
          title={t("grad_roots_tip", { count: roots })}
        >
          <Sprout className="w-3 h-3" />
          {roots}
        </span>
      )}
      <div className="flex items-center gap-2 flex-none">
        {lpWakeAt && (
          <span className="text-[10.5px] text-muted-foreground/70">
            {t("cockpit_wakes", {
              when: new Intl.DateTimeFormat(i18n.language, {
                month: "short",
                timeZone: "UTC",
              }).format(new Date(lpWakeAt)),
            })}
          </span>
        )}
        <button
          disabled={busy}
          onClick={(e) => {
            e.stopPropagation();
            onPromote();
          }}
          className="inline-flex items-center justify-center min-h-[44px] px-2 text-[12px] text-muted-foreground hover:text-foreground underline underline-offset-2 disabled:opacity-50"
        >
          {t("cockpit_wake")}
        </button>
      </div>
    </div>
  );
};

// ── Create / recalibrate dialog ───────────────────────────────────────────────

export const ProjectDialog: React.FC<{
  open: boolean;
  onOpenChange: (o: boolean) => void;
  editing: Pwf | null;
  promotedCount: number;
  /** Cap used for auto-placement of a new project; defaults to the constant. */
  activeCap?: number;
  onSaved: () => void;
}> = ({ open, onOpenChange, editing, promotedCount, activeCap = ACTIVE_CAP, onSaved }) => {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const [title, setTitle] = useState("");
  const [lens, setLens] = useState<Lens>(DEFAULT_LENS);
  const [wakeAt, setWakeAt] = useState("");
  const [saving, setSaving] = useState(false);

  // Sync form when opening
  const [openedFor, setOpenedFor] = useState<string | null>(null);
  if (open && openedFor !== (editing?._id ?? "new")) {
    setOpenedFor(editing?._id ?? "new");
    setTitle(editing?.title ?? "");
    setLens(editing ? lensOf(lifeProjectOf(editing)!) : DEFAULT_LENS);
    setWakeAt(editing ? lifeProjectOf(editing)?.wakeAt?.slice(0, 10) ?? "" : "");
  }
  if (!open && openedFor !== null) setOpenedFor(null);

  const placement = suggestPlacement(lens);

  const save = async () => {
    const name = title.trim();
    if (!name || saving) return;
    setSaving(true);
    try {
      const wakeAtIso = wakeAt ? new Date(wakeAt).toISOString() : undefined;
      if (editing) {
        await updatePwf(retryFetch, editing._id, {
          title: name,
          config: { lifeProject: { ...lifeProjectOf(editing)!, lens, wakeAt: wakeAtIso } },
        });
      } else {
        const status =
          placement === "active" && promotedCount < activeCap ? "promote" : "someday";
        await createPwf(retryFetch, {
          title: name,
          pwfType: "pwf",
          lifecycleState: "active",
          config: { lifeProject: { lens, status, wakeAt: wakeAtIso } },
        });
        toast.success(
          status === "promote" ? t("cockpit_created_active") : t("cockpit_created_resting")
        );
      }
      onOpenChange(false);
      onSaved();
    } catch (e: any) {
      toast.error(e.message ?? "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-3xl">
        <DialogHeader>
          <DialogTitle>
            {editing ? t("cockpit_recalibrate_title") : t("cockpit_new_project")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("cockpit_project_placeholder")}
            className="rounded-xl"
          />
          <p className="text-xs text-muted-foreground">{t("cockpit_lens_hint")}</p>
          {LENS_KEYS.map((k) => (
            <div key={k}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-foreground">{t(`cockpit_q_${k}`)}</span>
                <span className="text-xs font-semibold text-primary">{lens[k]}</span>
              </div>
              <Slider
                value={[lens[k]]}
                min={1}
                max={5}
                step={1}
                onValueChange={([v]) => setLens((s) => ({ ...s, [k]: v }))}
              />
            </div>
          ))}
          <div className="rounded-xl bg-primary/5 px-4 py-2.5 space-y-1">
            <p className="text-sm text-foreground">
              {placement === "active"
                ? t("cockpit_suggested_active")
                : t("cockpit_suggested_resting")}
            </p>
            {lens.pull <= 2 && (
              <p className="text-xs text-muted-foreground">{t("cockpit_low_pull_hint")}</p>
            )}
            {(editing ? lifeProjectOf(editing)!.status === "someday" : placement === "resting") && (
              <label className="block text-xs text-muted-foreground mt-3">
                {t("cockpit_wake_label")}
                <input
                  type="date"
                  value={wakeAt}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setWakeAt(e.target.value)}
                  className="mt-1 block rounded-lg border border-border/70 bg-background px-2.5 py-1.5 text-sm min-h-[44px]"
                />
              </label>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={!title.trim() || saving} className="rounded-full">
            {editing ? t("save") : t("cockpit_create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LifeProjects;
